/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Registrations Hooks - React Query for Registrations
 * ============================================================================
 *
 * Custom hooks for registration request data fetching and mutations using React Query.
 * Connected to real API endpoints.
 *
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { registrationsService } from '@/services/superadmin/registrations.service';
import { Registration, RegistrationFilterParams, RegistrationActionRequest } from '@/types/superadmin.types';
import { ApiError, PaginatedResponse, PaginationParams } from '@/services/api/types';

// Query keys for cache management
export const registrationKeys = {
  all: ['registrations'] as const,
  lists: () => [...registrationKeys.all, 'list'] as const,
  list: (params?: PaginationParams & RegistrationFilterParams) =>
    [...registrationKeys.lists(), params] as const,
  details: () => [...registrationKeys.all, 'detail'] as const,
  detail: (id: string) => [...registrationKeys.details(), id] as const,
  stats: () => [...registrationKeys.all, 'stats'] as const,
};

/**
 * Hook to fetch paginated registration requests
 */
export function useRegistrations(params?: PaginationParams & RegistrationFilterParams) {
  return useQuery<PaginatedResponse<Registration>, ApiError>({
    queryKey: registrationKeys.list(params),
    queryFn: () => registrationsService.getRegistrations(params),
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to fetch single registration detail
 */
export function useRegistration(id: string) {
  return useQuery<Registration, ApiError>({
    queryKey: registrationKeys.detail(id),
    queryFn: () => registrationsService.getRegistration(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch registration statistics
 */
export function useRegistrationStats() {
  return useQuery<
    {
      pendingCount: number;
      approvedThisMonth: number;
      rejectedThisMonth: number;
      totalProcessed: number;
    },
    ApiError
  >({
    queryKey: registrationKeys.stats(),
    queryFn: () => registrationsService.getRegistrationStats(),
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for approving registration
 */
export function useApproveRegistration() {
  const queryClient = useQueryClient();

  return useMutation<Registration, ApiError, { id: string; data?: RegistrationActionRequest }>({
    mutationFn: ({ id, data }) => registrationsService.approveRegistration(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: registrationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: registrationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: registrationKeys.stats() });
    },
  });
}

/**
 * Hook for rejecting registration
 */
export function useRejectRegistration() {
  const queryClient = useQueryClient();

  return useMutation<Registration, ApiError, { id: string; data?: RegistrationActionRequest }>({
    mutationFn: ({ id, data }) => registrationsService.rejectRegistration(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: registrationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: registrationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: registrationKeys.stats() });
    },
  });
}
